import { toast } from 'sonner';
import { customerRowsHtml, PARTY_CSS } from './customer';

// The printed documents. They live here rather than on a page because the
// invoice list, the delivery note list and a customer's profile all print the
// same paper, and it must not drift between them.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const taxLabel = (invoice) => {
  if (invoice.tax_rate === null || invoice.tax_rate === undefined) return 'Tax (18%)';  // pre-register invoices
  return `${invoice.tax_name || 'Tax'} (${invoice.tax_rate}%)`;
};

// An untaxed invoice says nothing about tax at all — no zero line on the
// document or in the form.
export const isTaxed = (invoice) =>
  Number(invoice.tax_rate || 0) > 0 || Number(invoice.tax_amount || 0) > 0;

// Images are embedded as data URIs so print windows never race the image load
async function toDataUrl(url) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

let __logoDataUrl = null;
async function getLogoDataUrl() {
  if (__logoDataUrl) return __logoDataUrl;
  try {
    const res = await fetch('/himan-logo.png');
    const blob = await res.blob();
    __logoDataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch {
    __logoDataUrl = `${window.location.origin}/himan-logo.png`;
  }
  return __logoDataUrl;
}

export async function printInvoice(invoice, { customers = [], paymentAccounts = [], defaultTerms = '' } = {}) {
    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) { toast.error('Popup blocked — allow popups to print'); return; }
    const logoSrc = await getLogoDataUrl();
    // Invoices raised before the payment register existed carry no accounts of
    // their own, so the ones in force today are shown — the customer still has
    // to be told where to send the money.
    const currentAccounts = paymentAccounts.filter(a => a.is_default);
    const invoiceAccounts = (invoice.payment_accounts || []).length
      ? invoice.payment_accounts
      : (currentAccounts.length ? currentAccounts : paymentAccounts);
    const terms = invoice.payment_terms || defaultTerms;
    // Payment QR codes are pulled in before the window opens, for the same reason
    const accountsWithQr = await Promise.all(invoiceAccounts.map(async (account) => ({
      ...account,
      // Only a Lipa Namba is worth scanning; a bank account is typed into a
      // banking app, and older invoices may still carry a code for one.
      qr_data: account.qr_url && account.mode === 'mobile_money'
        ? await toDataUrl(`${BACKEND_URL}${account.qr_url}`)
        : null,
    })));
    // Prefer embedded snapshot; fall back to live customer lookup
    const snapshot = invoice.customer_details || {};
    const live = customers.find(c => c.id === invoice.customer_id) || {};
    const customerBlock = customerRowsHtml(snapshot, live);
    
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${invoice.invoice_type === 'proforma' ? 'Proforma Invoice' : 'Invoice'} - ${invoice.invoice_number}</title>
        <style>
          body { font-family: 'Montserrat', Arial, sans-serif; padding: 40px; color: #010101; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #8FC043; padding-bottom: 20px; }
          .logo { max-width: 100px; margin: 0 auto 15px; }
          .company-name { color: #8FC043; font-weight: 700; font-size: 24px; margin: 10px 0; }
          .info { margin-bottom: 20px; }
          ${PARTY_CSS}
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background-color: #8FC043; color: white; font-weight: 600; }
          .total { text-align: right; font-weight: bold; color: #010101; }
          .highlight { color: #EA5829; font-weight: 600; }
          .payment { margin-top: 24px; border-top: 1px solid #ddd; padding-top: 12px; }
          .payment p { margin: 2px 0; font-size: 13px; }
          .payment .account { margin: 10px 0; display: flex; align-items: flex-start; gap: 16px; }
          .payment .account-text { flex: 1; }
          .payment .qr { width: 96px; height: 96px; }
          .payment .reference { margin-top: 10px; font-style: italic; color: #555; }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${logoSrc}" alt="Himan Logo" class="logo" />
          <div class="company-name">Himan Investment & Company</div>
          <h2 style="color: #EA5829;">${invoice.invoice_type === 'proforma' ? 'Proforma Invoice' : 'Invoice'}</h2>
          <p><strong>Invoice Number:</strong> ${invoice.invoice_number}</p>
          <p><strong>Date:</strong> ${new Date(invoice.created_at).toLocaleDateString()}</p>
        </div>
        <div class="info">
          ${customerBlock}
        </div>
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            ${invoice.items.map(item => {
              const qty = Number(item.quantity ?? 0);
              const unit = Number(item.unit_price ?? 0);
              const amt = Number(item.amount ?? item.total_price ?? qty * unit);
              return `
              <tr>
                <td>${item.product_name || ''}</td>
                <td>${qty}</td>
                <td>Tsh ${unit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td>Tsh ${amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
        <div class="total">
          <p>Subtotal: Tsh ${Number(invoice.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          ${isTaxed(invoice) ? `<p>${taxLabel(invoice)}: Tsh ${Number(invoice.tax_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>` : ''}
          <p><strong>Total: Tsh ${Number(invoice.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></p>
        </div>
        ${terms ? `<p><strong>Payment Terms:</strong> ${terms}</p>` : ''}
        ${accountsWithQr.length ? `
        <div class="payment">
          <p><strong>Payment Details</strong></p>
          ${accountsWithQr.map(account => `
            <div class="account">
              <div class="account-text">
                <p><strong>${account.mode_label || account.mode || ''}</strong>${account.provider ? ` — ${account.provider}` : ''}</p>
                ${account.account_name ? `<p>${account.mode === 'cheque' ? 'Payable to' : 'Account name'}: ${account.account_name}</p>` : ''}
                ${account.account_number ? `<p>${account.mode === 'mobile_money' ? 'Lipa Namba' : 'Account number'}: ${account.account_number}</p>` : ''}
                ${account.branch ? `<p>Branch: ${account.branch}</p>` : ''}
                ${account.swift_code ? `<p>SWIFT: ${account.swift_code}</p>` : ''}
                ${account.instructions ? `<p>${account.instructions}</p>` : ''}
              </div>
              ${account.qr_data ? `<img class="qr" src="${account.qr_data}" alt="Scan to pay" />` : ''}
            </div>`).join('')}
          <p class="reference">Please quote invoice ${invoice.invoice_number} as your payment reference.</p>
        </div>` : ''}
        ${invoice.notes ? `<p><strong>Notes:</strong> ${invoice.notes}</p>` : ''}
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    const doPrint = () => {
      if (printWindow.__printed) return;
      printWindow.__printed = true;
      printWindow.focus();
      printWindow.print();
    };
    printWindow.onload = doPrint;
    setTimeout(doPrint, 800);
  }

export async function printDeliveryNote(note, { customers = [] } = {}) {
    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) { toast.error('Popup blocked — allow popups to print'); return; }
    const logoSrc = await getLogoDataUrl();
    const snapshot = note.customer_details || {};
    const live = customers.find((c) => c.id === note.customer_id) || {};
    // Same bill-to presentation as the invoice; the note prints its own
    // delivery address below, so the customer's address row is left out.
    const customer = { address: snapshot.address || live.address || '' };
    const customerBlock = customerRowsHtml(
      { ...snapshot, delivery_point: note.delivery_point || snapshot.delivery_point },
      live,
      { includeAddress: false }
    );
    
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Delivery Note - ${note.delivery_number}</title>
        <style>
          body { font-family: 'Montserrat', Arial, sans-serif; padding: 40px; color: #010101; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #8FC043; padding-bottom: 20px; }
          .logo { max-width: 100px; margin: 0 auto 15px; }
          .company-name { color: #8FC043; font-weight: 700; font-size: 24px; margin: 10px 0; }
          .info { margin-bottom: 20px; }
          ${PARTY_CSS}
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background-color: #8FC043; color: white; font-weight: 600; }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${logoSrc}" alt="Himan Logo" class="logo" />
          <div class="company-name">Himan Investment & Company</div>
          <h2 style="color: #EA5829;">Delivery Note</h2>
          <p><strong>Delivery Number:</strong> ${note.delivery_number}</p>
          <p><strong>Date:</strong> ${new Date(note.created_at).toLocaleDateString()}</p>
        </div>
        <div class="info">
          ${customerBlock}
          <p><strong>Delivery Address:</strong> ${note.delivery_address || customer.address || 'N/A'}</p>
          <p><strong>Delivery Date:</strong> ${note.delivery_date || 'N/A'}</p>
          <p><strong>Vehicle Number:</strong> ${note.vehicle_number || 'N/A'}</p>
          <p><strong>Driver Name:</strong> ${note.driver_name || 'N/A'}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
            ${note.items.map(item => `
              <tr>
                <td>${item.product_name}</td>
                <td>${item.quantity}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        ${note.notes ? `<p><strong>Notes:</strong> ${note.notes}</p>` : ''}
        <div style="margin-top: 50px;">
          <p><strong>Received By:</strong> _____________________</p>
          <p><strong>Signature:</strong> _____________________</p>
          <p><strong>Date:</strong> _____________________</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    const doPrint = () => {
      if (printWindow.__printed) return;
      printWindow.__printed = true;
      printWindow.focus();
      printWindow.print();
    };
    printWindow.onload = doPrint;
    setTimeout(doPrint, 800);
  }

// An order confirmation the customer can keep — the same paper as the invoice,
// minus anything that would make it look like a tax document.
export async function printOrder(order, { customer = {} } = {}) {
  const printWindow = window.open('', '', 'width=800,height=600');
  if (!printWindow) { toast.error('Popup blocked — allow popups to print'); return; }
  const logoSrc = await getLogoDataUrl();
  const account = order.payment_account || null;
  const items = order.items || [];
  const money = (value) => `Tsh ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const content = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Order ${order.order_number}</title>
      <style>
        body { font-family: 'Montserrat', Arial, sans-serif; padding: 40px; color: #010101; }
        .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #8FC043; padding-bottom: 20px; }
        .logo { max-width: 100px; margin: 0 auto 15px; }
        .company-name { color: #8FC043; font-weight: 700; font-size: 24px; margin: 10px 0; }
        .info { margin-bottom: 20px; }
        .info p { margin: 2px 0; }
        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
        th { background-color: #8FC043; color: white; font-weight: 600; }
        .total { text-align: right; font-weight: bold; }
        .payment { margin-top: 24px; border-top: 1px solid #ddd; padding-top: 12px; font-size: 13px; }
      </style>
    </head>
    <body>
      <div class="header">
        <img src="${logoSrc}" alt="Himan Logo" class="logo" />
        <div class="company-name">Himan Investment &amp; Company</div>
        <h2 style="color: #EA5829;">Order Confirmation</h2>
        <p><strong>Order Number:</strong> ${order.order_number}</p>
        <p><strong>Date:</strong> ${new Date(order.created_at).toLocaleDateString()}</p>
      </div>
      <div class="info">
        <p><strong>Customer:</strong> ${customer.name || order.customer_name || ''}</p>
        ${order.delivery_address ? `<p><strong>Delivery address:</strong> ${order.delivery_address}</p>` : ''}
        ${order.status ? `<p><strong>Status:</strong> ${order.status}</p>` : ''}
        ${order.payment_reference ? `<p><strong>Payment reference:</strong> ${order.payment_reference}</p>` : ''}
      </div>
      <table>
        <thead>
          <tr><th>Product</th><th>Quantity</th><th>Unit Price</th><th>Amount</th></tr>
        </thead>
        <tbody>
          ${items.map((item) => {
            const quantity = Number(item.quantity || 0);
            const unit = Number(item.unit_price || 0);
            return `<tr>
              <td>${item.product_name || ''}</td>
              <td>${quantity}</td>
              <td>${money(unit)}</td>
              <td>${money(item.total_price ?? item.amount ?? quantity * unit)}</td>
            </tr>`;
          }).join('')}
        </tbody>
      </table>
      <div class="total"><p>Total: ${money(order.total_amount)}</p></div>
      ${account ? `
      <div class="payment">
        <p><strong>Paid by ${account.mode_label || account.mode || ''}</strong>${account.provider ? ` — ${account.provider}` : ''}</p>
        ${account.account_number ? `<p>${account.mode === 'mobile_money' ? 'Lipa Namba' : 'Account number'}: ${account.account_number}</p>` : ''}
        ${account.instructions ? `<p>${account.instructions}</p>` : ''}
      </div>` : ''}
    </body>
    </html>
  `;

  printWindow.document.write(content);
  printWindow.document.close();
  const doPrint = () => {
    if (printWindow.__printed) return;
    printWindow.__printed = true;
    printWindow.focus();
    printWindow.print();
  };
  printWindow.onload = doPrint;
  setTimeout(doPrint, 800);
}
